import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronRight } from 'lucide-react';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Card, CardHeader } from '../../components/ui/Card';
import { Select } from '../../components/ui/Input';
import { InfoTip } from '../../components/ui/InfoTip';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Nil } from '../../components/ui/Table';
import { formatDayMonthYear, formatDayMonthYearTime, formatMonthYear } from '../../utils/dateFormat';
import { formatNumber } from '../../utils/numberFormat';
import { collapseCountryList, countryName, formatEuroCompact } from './format';
import { displayCompanyName } from './labels';
import { challengerRule, clampDiaryTo, endsWithin, isBidRelevantCpv, monthOffset, monthsInclusive } from './diaryRange';
import {
  CHALLENGER_BASIS_LABELS,
  END_SOURCE_LABELS,
  getReTenderDiary,
  getReTenderSummary,
  RE_TENDER_DIARY_MAX_MONTHS,
  type ReTenderChallenger,
  type ReTenderContract,
  type ReTenderDiaryParams,
} from '../../api/tenders';

/** Buyer markets the tender pipeline ingests (tenders DEFAULT_COUNTRIES). */
const COUNTRIES: Array<{ id: string; label: string }> = [
  { id: '', label: 'All countries' },
  { id: 'BE', label: 'Belgium' },
  { id: 'NL', label: 'Netherlands' },
  { id: 'FR', label: 'France' },
  { id: 'DE', label: 'Germany' },
  { id: 'IE', label: 'Ireland' },
  { id: 'LU', label: 'Luxembourg' },
  { id: 'GB', label: 'United Kingdom' },
];

const MIN_VALUES: Array<{ id: number; label: string }> = [
  { id: 0, label: 'Any value' },
  { id: 500_000, label: '≥ €500k' },
  { id: 1_000_000, label: '≥ €1M' },
  { id: 2_000_000, label: '≥ €2M' },
  { id: 5_000_000, label: '≥ €5M' },
];

type EndFilter = 'all' | 'explicit' | 'derived';

/** The month picker's choices: "2026-10" keyed, "October 2026" labelled. */
function monthChoices(fromOffset: number, toOffset: number): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  for (let i = fromOffset; i <= toOffset; i++) {
    const id = monthOffset(i);
    out.push({ id, label: formatMonthYear(`${id}-01T00:00:00`) });
  }
  return out;
}

// Contracts that already ended are left out (the API never reads before today), so a
// past month would only ever show an empty diary: the picker starts at this month.
const FROM_MONTHS = monthChoices(0, 36);
const TO_MONTHS = monthChoices(0, 48);

const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', GBP: '£', USD: '$' };

/** "€2.3M", "£850k"; other currencies keep their code. */
function formatValue(amount: number | null, currency: string | null): string | null {
  if (amount === null || amount <= 0) return null;
  const cur = (currency ?? 'EUR').toUpperCase();
  return formatEuroCompact(Math.round(amount), CURRENCY_SYMBOL[cur] ?? `${cur} `);
}

/** Challenger chips: the name and the rule that picked it (lost bid, panel seat, frequent winner). */
function ChallengerChips({ challengers, total }: { challengers: ReTenderChallenger[]; total?: number | undefined }) {
  if (challengers.length === 0) return <span className="text-body-xs text-text-tertiary">none known</span>;
  const more = total !== undefined ? total - challengers.length : 0;
  return (
    <>
      {challengers.map((p) => (
        <span key={`c-${p.name}`} title={`${CHALLENGER_BASIS_LABELS[p.basis]}${p.awards ? ` (${p.awards})` : ''}`}>
          <Badge>
            {displayCompanyName(p.name)}
            <span className="text-text-tertiary"> · {challengerRule(p)}</span>
          </Badge>
        </span>
      ))}
      {more > 0 && (
        <span className="text-body-xs text-text-tertiary" title="The strongest are shown: lost bids first, then panel seats, then frequent winners">
          +{more} more
        </span>
      )}
    </>
  );
}

/** The Sourcing tiles' horizon: contracts ending in the next 90 days. */
const TILE_DAYS = 90;

/**
 * Default span: the months the tiles' 90 days touch (this month to the one
 * holding today + 90 days). Wider spans are the pickers' job.
 * The server reads at most RE_TENDER_DIARY_MAX_MONTHS
 * per request, one month at a time under a statement timeout: an 18-month
 * request (12 Oct 2026) held a database connection past the gateway limit.
 */

/**
 * The diary as a promise that fails loudly: a response without `months` (a
 * gateway page, an old build) is an error, never "no contracts", and a
 * gateway time-out says what to do about it.
 */
async function loadDiary(params: ReTenderDiaryParams) {
  let data;
  try {
    data = await getReTenderDiary(params);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (error instanceof Error && /\b(504|502|408)\b|time-?out|timed out/i.test(message)) {
      // Keep the original error (and its stack); only the message gets the advice.
      error.message = `The diary took longer than the 60-second limit to load (${message}). Narrow the months, pick a country or raise the minimum value.`;
    }
    throw error;
  }
  if (!data || !Array.isArray(data.months)) {
    throw new Error('The diary came back without any months: the response was not a diary. Try again, or narrow the range.');
  }
  return data;
}

function challengerKey(c: ReTenderContract): string {
  const names = c.challengers
    .map((p) => `${p.name}|${p.basis}`)
    .sort()
    .join(';');
  return `${names}#${c.challengerCount ?? c.challengers.length}`;
}

/** The challenger list when every contract in the month shares it, so it is shown once, not per row. */
function sharedChallengers(contracts: ReTenderContract[]): ReTenderChallenger[] | null {
  if (contracts.length < 2 || contracts[0]!.challengers.length === 0) return null;
  const key = challengerKey(contracts[0]!);
  return contracts.every((c) => challengerKey(c) === key) ? contracts[0]!.challengers : null;
}

/** "€12.4M" — one figure per currency, so mixed months never add pounds to euros. */
function monthTotal(contracts: ReTenderContract[]): string | null {
  const byCur = new Map<string, number>();
  for (const c of contracts) {
    if (c.valueAmount === null || c.valueAmount <= 0) continue;
    const cur = (c.valueCurrency ?? 'EUR').toUpperCase();
    byCur.set(cur, (byCur.get(cur) ?? 0) + c.valueAmount);
  }
  if (byCur.size === 0) return null;
  return [...byCur.entries()]
    .sort(([a], [b]) => (a === 'EUR' ? -1 : b === 'EUR' ? 1 : a.localeCompare(b)))
    .map(([cur, amount]) => formatValue(amount, cur))
    .filter(Boolean)
    .join(' + ');
}

/** Fixed tracks so the value lines up from row to row: end date · title · value · framework. */
const LINE_GRID = 'grid grid-cols-[6.5rem_minmax(0,1fr)_5.5rem_5.5rem] items-baseline gap-x-3';

function ContractLine({ c, hideChallengers }: { c: ReTenderContract; hideChallengers: boolean }) {
  const value = formatValue(c.valueAmount, c.valueCurrency);
  const derived = c.endSource !== 'explicit';
  // TED titles open with the buyer's country ("Germany – Database systems – …"), which the line below already says.
  const country = c.buyerCountry ? countryName(c.buyerCountry) : null;
  const rawTitle = c.title ?? c.ref;
  const title = collapseCountryList(
    country && rawTitle.startsWith(`${country} – `) ? rawTitle.slice(country.length + 3) : rawTitle,
  );
  return (
    <li className="space-y-1.5 border-b border-border-light py-3 last:border-b-0">
      <div className={LINE_GRID}>
        <span
          className="whitespace-nowrap text-body-xs tabular-nums text-text-secondary"
          title={derived ? `End derived: ${END_SOURCE_LABELS[c.endSource ?? ''] ?? 'source unknown'}` : END_SOURCE_LABELS.explicit}
        >
          {c.endDate ? formatDayMonthYear(`${c.endDate.slice(0, 10)}T00:00:00`) : <Nil />}
          {derived && <sup className="ml-0.5 text-text-muted">≈</sup>}
        </span>
        <span className="min-w-0 text-body-sm text-text-primary" title={title.full ?? undefined}>
          {c.noticeUrl ? (
            <a href={c.noticeUrl} target="_blank" rel="noreferrer" className="hover:underline">
              {title.text}
            </a>
          ) : (
            title.text
          )}
        </span>
        <span className="whitespace-nowrap text-right text-body-xs tabular-nums text-text-secondary">
          {value ?? (
            <span className="text-text-muted" title="The notice gives no value, or a placeholder such as EUR 1">
              not published
            </span>
          )}
        </span>
        <span className="text-right">{c.isFramework && <Badge>Framework</Badge>}</span>
      </div>
      <p className="text-body-xs text-text-tertiary">
        {c.buyerName ? displayCompanyName(c.buyerName) : 'Unknown buyer'}
        {c.buyerCountry ? ` · ${countryName(c.buyerCountry)}` : ''}
        {c.cpvDivision ? ` · CPV ${c.cpvDivision}` : ''}
        {c.bidsReceived !== null ? ` · ${c.bidsReceived} bid${c.bidsReceived === 1 ? '' : 's'} last time` : ''}
      </p>
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-body-xs text-text-tertiary">Incumbent</span>
        {c.incumbents.length === 0 ? (
          <span className="text-body-xs text-text-muted" title="The notice names no supplier, or only a placeholder such as “See attached”">
            unknown
          </span>
        ) : (
          c.incumbents.map((p) => (
            <span key={`i-${p.name}`} title={p.matchedCompanyId ? 'A company we already track' : undefined}>
              <Badge variant={p.matchedCompanyId ? 'pill' : 'default'} dot={!!p.matchedCompanyId}>
                {displayCompanyName(p.name)}
              </Badge>
            </span>
          ))
        )}
        {!hideChallengers && (
          <>
            <span className="ml-2 text-body-xs text-text-tertiary">Challengers</span>
            <ChallengerChips challengers={c.challengers} total={c.challengerCount} />
          </>
        )}
      </div>
    </li>
  );
}

/**
 * GTM → Re-tender diary.
 *
 * Every public contract whose end falls in the chosen months, grouped by end
 * month: who holds it (the S04A "defend" audience) and who is placed to take
 * it (S04B "challenge": bid last time, sits on the buyer's panel, or keeps
 * winning similar contracts). Ends marked ≈ are derived from a start or award
 * date plus the contract duration, not printed on the notice. Incumbents with
 * a dot are companies we already track.
 */
export function DiaryView() {
  const [from, setFromState] = useState(() => monthOffset(0));
  // The month holding today + 90 days: the diary opens on the window the Sourcing tiles count.
  const [toChoice, setToState] = useState(() => monthOffset(Math.ceil(TILE_DAYS / 30)));
  // Until the reader picks months, the list stops on the tiles' last day, not at the end of that month.
  const [tileWindow, setTileWindow] = useState(true);
  const setFrom = (v: string) => {
    setTileWindow(false);
    setFromState(v);
  };
  const setTo = (v: string) => {
    setTileWindow(false);
    setToState(v);
  };
  const [bidOnly, setBidOnly] = useState(true);
  const summary = useQuery({ queryKey: ['tenders', 're-tender-summary'], queryFn: getReTenderSummary });
  const windowTo = summary.data?.windowTo ?? new Date(Date.now() + TILE_DAYS * 86_400_000).toISOString().slice(0, 10);
  // The server refuses more than RE_TENDER_DIARY_MAX_MONTHS: the picker never asks for it.
  const to = clampDiaryTo(from, toChoice);
  const toChoices = TO_MONTHS.filter((m) => {
    const span = monthsInclusive(from, m.id);
    return span >= 1 && span <= RE_TENDER_DIARY_MAX_MONTHS;
  });
  const [country, setCountry] = useState('');
  const [minValue, setMinValue] = useState(0);
  const [endFilter, setEndFilter] = useState<EndFilter>('all');
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const params: ReTenderDiaryParams = { from, to, ...(country ? { country } : {}), ...(minValue > 0 ? { minValue } : {}) };
  const diary = useQuery({
    queryKey: ['tenders', 're-tender-diary', params],
    queryFn: () => loadDiary(params),
    // One retry at most: three silent retries of a 60 s query kept the spinner up for minutes.
    retry: 1,
    enabled: /^\d{4}-\d{2}$/.test(from) && /^\d{4}-\d{2}$/.test(to),
  });

  // Contracts in the range after the window cut (the tiles' 90 days), before the relevance and end-source filters.
  const inRange = useMemo(
    () =>
      (diary.data?.months ?? [])
        .map((m) => ({ ...m, contracts: tileWindow ? m.contracts.filter((c) => endsWithin(c.endDate, windowTo)) : m.contracts }))
        .filter((m) => m.contracts.length > 0),
    [diary.data, tileWindow, windowTo],
  );
  const irrelevantHidden = useMemo(
    () => (bidOnly ? inRange.reduce((n, m) => n + m.contracts.filter((c) => !isBidRelevantCpv(c.cpvDivision)).length, 0) : 0),
    [inRange, bidOnly],
  );
  // Euros of the hidden non-bid contracts: what the Sourcing tile counts on top of this list.
  const hiddenEur = useMemo(
    () =>
      bidOnly
        ? inRange.reduce(
            (n, m) =>
              n +
              m.contracts
                .filter((c) => !isBidRelevantCpv(c.cpvDivision) && (c.valueCurrency ?? 'EUR').toUpperCase() === 'EUR')
                .reduce((k, c) => k + (c.valueAmount ?? 0), 0),
            0,
          )
        : 0,
    [inRange, bidOnly],
  );
  const months = useMemo(() => {
    return inRange
      .map((m) => ({
        ...m,
        contracts: m.contracts.filter(
          (c) =>
            (!bidOnly || isBidRelevantCpv(c.cpvDivision)) &&
            (endFilter === 'all' || (endFilter === 'explicit' ? c.endSource === 'explicit' : c.endSource !== 'explicit')),
        ),
      }))
      .filter((m) => m.contracts.length > 0);
  }, [inRange, endFilter, bidOnly]);

  const loadedAny = (diary.data?.months.length ?? 0) > 0;
  const incompleteLabel = diary.data?.incompleteFrom
    ? formatMonthYear(`${diary.data.incompleteFrom}-01T00:00:00`)
    : null;
  const shown = months.reduce((n, m) => n + m.contracts.length, 0);
  const contracts = months.flatMap((m) => m.contracts);
  const eurTotal = contracts
    .filter((c) => (c.valueCurrency ?? 'EUR').toUpperCase() === 'EUR' && (c.valueAmount ?? 0) > 0)
    .reduce((n, c) => n + (c.valueAmount ?? 0), 0);
  const unpublished = contracts.filter((c) => !(c.valueAmount && c.valueAmount > 0)).length;
  const trackedIncumbent = contracts.filter((c) => c.incumbents.some((p) => p.matchedCompanyId)).length;
  const rangeLabel = tileWindow
    ? `next ${TILE_DAYS} days, to ${formatDayMonthYear(`${windowTo.slice(0, 10)}T00:00:00`)}`
    : `${formatMonthYear(`${from}-01T00:00:00`)} – ${formatMonthYear(`${to}-01T00:00:00`)}`;
  const isOpen = (month: string, index: number) => open[month] ?? index === 0;

  return (
    <div className="space-y-4">
      <Card padding="sm" className="space-y-4">
        <CardHeader
          title="Contracts by the month they end"
          description={
            diary.data
              ? // The Sourcing tiles above already answer "how much ends in 90 days"; this states the range shown.
                [
                  `${formatNumber(shown)} contracts · ${rangeLabel}${diary.data.capped ? ' · capped' : ''}${
                    diary.data.incompleteFrom ? ' · incomplete' : ''
                  }`,
                  `${formatEuroCompact(eurTotal) || '€0'} value in euro-priced contracts${
                    unpublished > 0 ? ` (${formatNumber(unpublished)} with no published value)` : ''
                  }`,
                  irrelevantHidden > 0 ? `${formatNumber(irrelevantHidden)} hidden as not bid-team work (${formatEuroCompact(hiddenEur) || '€0'}): the Sourcing tile counts them, so its € is this plus that` : null,
                  trackedIncumbent > 0 ? `${formatNumber(trackedIncumbent)} held by a company in our CRM` : null,
                  // The server reuses an answer for up to 15 minutes; say when it was read.
                  diary.data.cachedAt ? `read ${formatDayMonthYearTime(diary.data.cachedAt)}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'The incumbent, and the companies placed to challenge it.'
          }
          action={
            <InfoTip>
              The same data feeds the “Re-tender · defend” and “Re-tender · challenge” plays for contracts ending 6–18
              months out. The view opens on the next {TILE_DAYS} days, the same window as the Sourcing tiles; widen it with the month pickers, up to{' '}
              {RE_TENDER_DIARY_MAX_MONTHS} months at a time. Dates marked ≈ are the start or award date plus the contract duration, not printed on the
              notice. Incumbents with a dot are companies we already track. Each challenger names the rule that picked it:
              “lost bid” (named as a losing tenderer on this contract), “panel” (holds a framework seat with this buyer
              for the same main work) or “won N similar” (N awards in the same country and main work, last 24 months).
            </InfoTip>
          }
        />

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-body-xs text-text-tertiary">
            From
            <Select size="sm" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From month">
              {FROM_MONTHS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex items-center gap-1.5 text-body-xs text-text-tertiary">
            To
            <Select size="sm" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To month">
              {toChoices.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </Select>
          </label>
          <Select size="sm" value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Buyer country">
            {COUNTRIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
          <Select size="sm" value={minValue} onChange={(e) => setMinValue(Number(e.target.value))} aria-label="Minimum value">
            {MIN_VALUES.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </Select>
          <Select size="sm" value={endFilter} onChange={(e) => setEndFilter(e.target.value as EndFilter)} aria-label="End date source">
            <option value="all">Any end source</option>
            <option value="explicit">End date on the notice</option>
            <option value="derived">Derived end only</option>
          </Select>
          <Select size="sm" value={bidOnly ? 'bid' : 'all'} onChange={(e) => setBidOnly(e.target.value === 'bid')} aria-label="Relevance">
            <option value="bid">Bid-team work only</option>
            <option value="all">All contracts</option>
          </Select>
        </div>

        {diary.isLoading ? (
          <LoadingState rows={5} label="Loading contracts: this view can take a while…" />
        ) : diary.isError || !diary.isSuccess ? (
          // Anything but a real answer is an error (a 5xx, a time-out, a paused
          // or disabled query): never the "no contracts" empty state.
          <ErrorState
            title="Couldn’t load the diary"
            error={diary.error ?? new Error('The diary has not loaded. Retry, or narrow the range.')}
            onRetry={() => diary.refetch()}
          />
        ) : months.length === 0 && incompleteLabel ? (
          <Alert variant="warning">
            The diary ran out of time before it reached any contracts ({incompleteLabel} onward was not read). Pick a
            country, raise the minimum value or narrow the months.
          </Alert>
        ) : months.length === 0 ? (
          <EmptyState
            title={loadedAny ? 'No contracts match this end-source filter' : 'No contracts end in this range'}
            description={
              loadedAny
                ? 'Contracts end in this range, but none with the chosen end source. Set it back to “Any end source”.'
                : 'The diary only knows contracts whose award notice gave an end date or a duration.'
            }
            compact
          />
        ) : (
          <div className="space-y-2">
            <p className="text-body-xs text-text-tertiary">
              {/* The count is in the header; this line only explains the marks. */}
              Contracts that already ended are left out · <sup className="text-text-muted">≈</sup> end derived from a
              start or award date plus the duration
            </p>
            {incompleteLabel && (
              <Alert variant="warning">
                Only part of the range loaded: {incompleteLabel} onward took too long to read and is not shown. Pick a
                country, raise the minimum value or start the range later.
              </Alert>
            )}
            {diary.data?.capped && (
              <Alert variant="warning">
                More contracts end in this range than one view loads; the list stops at the cap. Narrow the months or raise the
                minimum value.
              </Alert>
            )}
            <div className="-mx-3 border-t border-border-light">
              {months.map((m, i) => {
                const shared = sharedChallengers(m.contracts);
                const total = monthTotal(m.contracts);
                const opened = isOpen(m.month, i);
                return (
                  <div key={m.month}>
                    {/* A flat group row, like a table's: the month, its count and value, then its contracts. */}
                    <button
                      type="button"
                      onClick={() => setOpen((o) => ({ ...o, [m.month]: !opened }))}
                      className="flex w-full items-baseline gap-x-3 border-b border-border-light bg-surface-secondary px-3 py-2 text-left transition-colors hover:bg-surface-tertiary/60"
                      aria-expanded={opened}
                    >
                      <ChevronRight
                        className={clsx('h-3.5 w-3.5 flex-none self-center text-text-muted transition-transform', opened && 'rotate-90')}
                        aria-hidden
                      />
                      <span className="text-body-sm font-semibold text-text-primary">{m.label}</span>
                      <span className="text-body-xs tabular-nums text-text-tertiary">
                        {m.contracts.length} contract{m.contracts.length === 1 ? '' : 's'}
                        {total && ` · ${total}`}
                      </span>
                    </button>
                    {opened && shared && (
                      <div className="flex flex-wrap items-center gap-1 border-b border-border-light px-3 py-2">
                        <span className="text-body-xs text-text-tertiary">Challengers on every contract this month</span>
                        <ChallengerChips challengers={shared} total={m.contracts[0]?.challengerCount} />
                      </div>
                    )}
                    {opened && (
                      <ul className="border-b border-border-light px-3">
                        {m.contracts.map((c) => (
                          <ContractLine key={c.ref} c={c} hideChallengers={!!shared} />
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
